import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowLeft, ChevronDown, Building, Star, MoreHorizontal, Edit, Lock, Unlock, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import OrganizationTile from "./OrganizationTile";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger 
} from "@/components/ui/popover";

const OrganizationSelector = ({ 
  selectedOrg, 
  onSelect,
  showBackButton = true,
}: { 
  selectedOrg: string | null;
  onSelect: (orgId: string) => void;
  showBackButton?: boolean;
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  
  const { data: organizations, isLoading, refetch } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data;
    },
    staleTime: 0,
    refetchOnMount: true,
  });

  const { data: currentOrganization } = useQuery({
    queryKey: ["organization", selectedOrg],
    queryFn: async () => {
      if (!selectedOrg) return null;
      
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", selectedOrg)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!selectedOrg,
  });
  
  const { data: defaultOrganization, refetch: refetchDefault } = useQuery({
    queryKey: ["default-organization"],
    queryFn: async () => {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data, error } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", user.id)
        .eq("is_default", true)
        .maybeSingle();
      
      if (error || !data) {
        console.log("No default organization found");
        return null;
      }
      
      return data?.organization_id;
    },
    staleTime: 0,
  });

  // Function to mark an organization as default
  const handleMarkDefault = async (orgId: string) => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication error",
          description: "You need to be logged in to perform this action",
          variant: "destructive",
        });
        return;
      }

      console.log("Setting default organization:", orgId, "for user:", user.id);
      
      // First, update all memberships to set is_default to false
      const { error: resetError } = await supabase
        .from("organization_members")
        .update({ is_default: false })
        .eq("user_id", user.id);
        
      if (resetError) {
        console.error("Error resetting previous defaults:", resetError);
        throw resetError;
      }
      
      // Then set the selected organization as default
      const { error: updateError } = await supabase
        .from("organization_members")
        .update({ is_default: true })
        .eq("organization_id", orgId)
        .eq("user_id", user.id);
      
      if (updateError) {
        console.error("Error setting default organization:", updateError);
        throw updateError;
      }
      
      toast({
        title: "Default organization set",
        description: "Your default organization has been set successfully",
        variant: "success",
      });
      
      // Refresh queries
      refetchDefault();
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["default-organization"] });
      queryClient.invalidateQueries({ queryKey: ["default-organization-redirect"] });
      queryClient.invalidateQueries({ queryKey: ["default-organization-nav"] });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to set default organization",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (orgId: string) => {
    try {
      // First check if organization has any data
      const { data: hasData, error: checkError } = await supabase
        .rpc('check_organization_has_data', {
          org_id: orgId
        });

      if (checkError) {
        throw checkError;
      }

      if (hasData) {
        toast({
          title: "Cannot Delete Organization",
          description: "This organization has associated data and cannot be deleted.",
          variant: "destructive",
        });
        return;
      }

      // If no data exists, proceed with deletion
      const { error: deleteError } = await supabase
        .from("organizations")
        .delete()
        .eq("id", orgId);

      if (deleteError) throw deleteError;

      toast({
        title: "Success",
        description: "Organization deleted successfully",
      });

      // Refresh the organizations list
      refetch();
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    } catch (error: any) {
      console.error('Delete error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete organization",
        variant: "destructive",
      });
    }
  };

  const handleToggleLock = async (orgId: string, currentLockStatus: boolean) => {
    try {
      const { error } = await supabase
        .from("organizations")
        .update({ is_locked: !currentLockStatus })
        .eq("id", orgId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Organization ${currentLockStatus ? 'unlocked' : 'locked'} successfully`,
      });

      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (orgId: string) => {
    navigate(`/app/${orgId}/settings`);
  };

  // Update selected organization if it's not valid
  useEffect(() => {
    if (!isLoading && organizations && organizations.length > 0 && selectedOrg === null) {
      // Check if there's a default organization
      if (defaultOrganization) {
        onSelect(defaultOrganization);
      } else {
        // Otherwise select the first organization
        onSelect(organizations[0].id);
      }
    }
  }, [isLoading, organizations, selectedOrg, defaultOrganization, onSelect]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {showBackButton && (
          <Button 
            variant="ghost" 
            className="gap-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        )}
        <div className="h-10 flex items-center justify-center">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {showBackButton && (
        <Button 
          variant="ghost" 
          className="gap-2 mb-4"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      )}
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-9 gap-2 px-2 lg:px-4 text-primary">
            <Building className="h-5 w-5 text-primary" />
            <span className="truncate max-w-[100px] sm:max-w-[150px] font-medium">
              {currentOrganization?.name || "Select Organization"}
            </span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" align={isMobile ? "center" : "start"}>
          {organizations?.map((org) => (
            <DropdownMenuItem 
              key={org.id} 
              className={`${selectedOrg === org.id ? 'bg-accent text-accent-foreground' : ''} justify-between`}
            >
              <div 
                className="flex-1 cursor-pointer"
                onClick={() => onSelect(org.id)}
              >
                {org.name || "Unnamed Organization"}
                {defaultOrganization === org.id && (
                  <span className="ml-2 text-primary text-xs">(Default)</span>
                )}
              </div>

              {/* Organization actions menu */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[160px] p-2">
                  <div className="grid gap-1">
                    {defaultOrganization !== org.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start text-sm"
                        onClick={() => handleMarkDefault(org.id)}
                      >
                        <Star className="mr-2 h-4 w-4" />
                        Mark as default
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-start text-sm"
                      onClick={() => handleEdit(org.id)}
                    >
                      <Edit className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-start text-sm"
                      onClick={() => handleToggleLock(org.id, org.is_locked || false)}
                    >
                      {org.is_locked ? (
                        <>
                          <Unlock className="mr-2 h-4 w-4" />
                          Unlock
                        </>
                      ) : (
                        <>
                          <Lock className="mr-2 h-4 w-4" />
                          Lock
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-start text-sm text-destructive hover:text-destructive"
                      onClick={() => handleDelete(org.id)}
                    >
                      <Trash className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem onClick={() => navigate("/create-organization")}>
            <Plus className="h-4 w-4 mr-2" />
            Create New Organization
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {!showBackButton && organizations?.length === 0 && (
        <div className="text-center text-muted-foreground py-4">
          No organizations found. Create your first one!
        </div>
      )}
    </div>
  );
};

export default OrganizationSelector;
